/**
 * Audio in-chat (PWA / browser): Web Audio + fallback HTML5.
 */

import { getEffectiveSoundGain, isSoundMuted } from '../storage/soundSettingsStore.ts'

let unlocked = false
let sharedAudioCtx: AudioContext | null = null
let unlockElement: HTMLAudioElement | null = null

const SILENT_WAV =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA'

let chatBeepDataUri: string | null = null

export function isNotificationAudioUnlocked(): boolean {
  return unlocked
}

function getAudioContextClass(): typeof AudioContext | null {
  if (typeof window === 'undefined') return null
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext ||
    null
  )
}

export async function resumeNotificationAudio(): Promise<AudioContext | null> {
  try {
    const Ctx = getAudioContextClass()
    if (!Ctx) return null
    if (!sharedAudioCtx || sharedAudioCtx.state === 'closed') {
      sharedAudioCtx = new Ctx()
    }
    if (sharedAudioCtx.state === 'suspended') {
      try {
        await sharedAudioCtx.resume()
      } catch {
        /* autoplay policy */
      }
    }
    if (sharedAudioCtx.state === 'running') unlocked = true
    return sharedAudioCtx
  } catch {
    return null
  }
}

/** Chiamare su gesture utente (impostazioni, tap chat, ecc.). */
export async function unlockNotificationAudio(): Promise<boolean> {
  const ctx = await resumeNotificationAudio()
  if (ctx?.state === 'running') {
    unlocked = true
    return true
  }

  try {
    const audio = unlockElement ?? new Audio(SILENT_WAV)
    unlockElement = audio
    audio.volume = 0.01
    await audio.play()
    audio.pause()
    audio.currentTime = 0
    audio.volume = 1
  } catch {
    unlocked = false
    return false
  }

  const ctx2 = await resumeNotificationAudio()
  if (ctx2?.state === 'running') {
    try {
      const osc = ctx2.createOscillator()
      const gain = ctx2.createGain()
      gain.gain.value = 0.02
      osc.connect(gain)
      gain.connect(ctx2.destination)
      const t0 = ctx2.currentTime
      osc.start(t0)
      osc.stop(t0 + 0.03)
    } catch {
      /* ignore */
    }
    unlocked = true
    return true
  }

  unlocked = false
  return false
}

function onFirstUserGesture(): void {
  void unlockNotificationAudio()
}

export function initAudioUnlock(): void {
  if (typeof window === 'undefined') return
  window.addEventListener('touchstart', onFirstUserGesture, { once: true, passive: true })
  window.addEventListener('click', onFirstUserGesture, { once: true })
  window.addEventListener('pointerdown', onFirstUserGesture, { once: true })
  window.addEventListener('keydown', onFirstUserGesture, { once: true })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void resumeNotificationAudio()
  })
}

function buildChatBeepDataUri(): string {
  if (chatBeepDataUri) return chatBeepDataUri
  const sampleRate = 44100
  const duration = 0.14
  const freq = 740
  const numSamples = Math.floor(sampleRate * duration)
  const buffer = new ArrayBuffer(44 + numSamples * 2)
  const view = new DataView(buffer)
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i))
  }
  writeStr(0, 'RIFF')
  view.setUint32(4, 36 + numSamples * 2, true)
  writeStr(8, 'WAVE')
  writeStr(12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, sampleRate, true)
  view.setUint32(28, sampleRate * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(36, 'data')
  view.setUint32(40, numSamples * 2, true)
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate
    const env = Math.exp(-t * 12)
    const sample = Math.sin(2 * Math.PI * freq * t) * 0.45 * env
    view.setInt16(44 + i * 2, Math.max(-32767, Math.min(32767, sample * 32767)), true)
  }
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  chatBeepDataUri = `data:audio/wav;base64,${btoa(binary)}`
  return chatBeepDataUri
}

function playOscillatorOnCtx(
  ctx: AudioContext,
  frequencyHz: number,
  durationSec: number,
  gainPeak: number,
): void {
  const osc = ctx.createOscillator()
  const gain = ctx.createGain()
  osc.type = 'sine'
  osc.frequency.value = frequencyHz
  osc.connect(gain)
  gain.connect(ctx.destination)
  const t0 = ctx.currentTime
  const peak = Math.max(0.001, gainPeak)
  gain.gain.setValueAtTime(peak, t0)
  gain.gain.exponentialRampToValueAtTime(0.001, t0 + durationSec)
  osc.start(t0)
  osc.stop(t0 + durationSec + 0.03)
  unlocked = true
}

async function playHtmlBeep(volume: number): Promise<boolean> {
  try {
    const audio = new Audio(buildChatBeepDataUri())
    audio.volume = Math.min(1, Math.max(0.05, volume * 3))
    await audio.play()
    unlocked = true
    return true
  } catch {
    return false
  }
}

/**
 * Suono in-chat / prova impostazioni.
 * Su click utente usa Web Audio; in background prova anche HTML5 Audio.
 */
export async function playAudibleFeedbackTone(
  frequencyHz = 740,
  durationSec = 0.14,
  peakGain = 0.14,
): Promise<boolean> {
  if (isSoundMuted()) return false
  const gainPeak = getEffectiveSoundGain(peakGain)
  if (gainPeak <= 0.0001) return false

  const ctx = await resumeNotificationAudio()
  if (ctx?.state === 'running') {
    try {
      playOscillatorOnCtx(ctx, frequencyHz, durationSec, gainPeak)
      return true
    } catch {
      /* fall through */
    }
  }

  return await playHtmlBeep(gainPeak)
}

export function playOscillatorTone(frequencyHz: number, durationSec: number, peakGain = 0.12): void {
  void playAudibleFeedbackTone(frequencyHz, durationSec, peakGain)
}

/** Piccolo beep con chat aperta (PWA / browser). */
export function playInChatIncomingTone(): void {
  void playAudibleFeedbackTone(740, 0.12, 0.14)
}
